export interface UseCases {
    id: number;
    role: string;
    description: string;
    features?: UseCaseFeatures[];
    status?: 1 | 0;
}

export interface UseCaseFeatures {
    id: number;
    use_case_id: number;
    icon: string;
    feature: string;
    description: string;
}

export const useCases: UseCases[] = [
    {
        id: 1,
        role: "Architecture",
        description: "Focuses on designing and planning buildings or structures, ensuring they meet both aesthetic and functional requirements.",
        features: [
            {
                id: 1,
                use_case_id: 1,
                icon: "/assets/images/website/use-case-page/feature-icons/photo_drawing.svg",
                feature: "Photo & Drawing",
                description: "Capture and annotate photos or drawings to document architectural details and site conditions efficiently."
            },
            {
                id: 2,
                use_case_id: 1,
                icon: "/assets/images/website/use-case-page/feature-icons/pdf-report.svg",
                feature: "PDF Report",
                description: "Generate comprehensive PDF reports of your architectural projects, including images, notes, and project details."
            },
            {
                id: 3,
                use_case_id: 1,
                icon: "/assets/images/website/use-case-page/feature-icons/send_report.svg",
                feature: "Send Report",
                description: "Easily share reports with clients or team members directly from the platform via email or other communication channels."
            },
            {
                id: 4,
                use_case_id: 1,
                icon: "/assets/images/website/use-case-page/feature-icons/import_export.svg",
                feature: "Import & Export Data",
                description: "Seamlessly import and export project data to and from various file formats, enabling data integration with other tools."
            }
        ],
        status: 1
    },
    {
        id: 2,
        role: "Contractor",
        description: "Manages construction projects, including overseeing the workforce, resources, and project timeline to ensure successful completion.",
        features: [
            {
                id: 5,
                use_case_id: 2,
                icon: "/assets/images/website/use-case-page/feature-icons/task_management.svg",
                feature: "Task Management",
                description: "Organize and assign tasks to team members, track progress, and manage deadlines effectively."
            },
            {
                id: 6,
                use_case_id: 2,
                icon: "/assets/images/website/use-case-page/feature-icons/resource_allocation.svg",
                feature: "Resource Allocation",
                description: "Efficiently allocate materials and equipment to the right tasks to avoid delays and optimize usage."
            },
            {
                id: 7,
                use_case_id: 2,
                icon: "/assets/images/website/use-case-page/feature-icons/cost_tracking.svg",
                feature: "Cost Tracking",
                description: "Track expenses and project costs in real-time to ensure the budget is maintained throughout the project."
            }
        ],
        status: 0
    },
    {
        id: 3,
        role: "Developer",
        description: "Invests in and manages real estate development projects, from initial planning and design to final construction and marketing.",
        features: [
            {
                id: 8,
                use_case_id: 3,
                icon: "/assets/images/website/use-case-page/feature-icons/project_overview.svg",
                feature: "Project Overview",
                description: "Get a holistic view of all ongoing projects, including timelines, budgets, and milestones."
            },
            {
                id: 9,
                use_case_id: 3,
                icon: "/assets/images/website/use-case-page/feature-icons/financial_planning.svg",
                feature: "Financial Planning",
                description: "Plan financial investments for different phases of construction and ensure proper fund allocation."
            }
        ],
        status: 0
    },
    {
        id: 4,
        role: "Project Manager",
        description: "Coordinates all aspects of a project, ensuring that it is completed on time, within budget, and to the required quality standards.",
        features: [
            {
                id: 10,
                use_case_id: 4,
                icon: "/assets/images/website/use-case-page/feature-icons/schedule_management.svg",
                feature: "Schedule Management",
                description: "Create and manage project schedules, track deadlines, and ensure timely project delivery."
            },
            {
                id: 11,
                use_case_id: 4,
                icon: "/assets/images/website/use-case-page/feature-icons/risk_analysis.svg",
                feature: "Risk Analysis",
                description: "Identify potential risks in the project and develop strategies to mitigate them."
            }
        ],
        status: 0
    },
    {
        id: 5,
        role: "Bank and Finance",
        description: "Provides financial services and support for real estate and construction projects, including loans, mortgages, and financial analysis.",
        features: [
            {
                id: 12,
                use_case_id: 5,
                icon: "/assets/images/website/use-case-page/feature-icons/loan_management.svg",
                feature: "Loan Management",
                description: "Manage loans and financial transactions related to construction projects with ease."
            },
            {
                id: 13,
                use_case_id: 5,
                icon: "/assets/images/website/use-case-page/feature-icons/financial_analysis.svg",
                feature: "Financial Analysis",
                description: "Analyze financial data to evaluate project viability and manage investments efficiently."
            }
        ],
        status: 0
    },
    {
        id: 6,
        role: "Commercial Client",
        description: "Represents the interests of the owner or stakeholder in commercial projects, providing input and making decisions on the project’s requirements and goals.",
        features: [
            {
                id: 14,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/progress_tracking.svg",
                feature: "Progress Tracking",
                description: "Monitor the progress of the project in real-time and stay informed about any updates."
            },
            {
                id: 15,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/document_review.svg",
                feature: "Document Review",
                description: "Review and approve documents, blueprints, and other project-related materials."
            },
            {
                id: 16,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/budget_management.svg",
                feature: "Budget Management",
                description: "Track and manage the project’s budget to ensure financial targets are met."
            }
        ],
        status: 1
    },
    {
        id: 7,
        role: "Domestic Client",
        description: "Represents the interests of homeowners or individuals, providing input and making decisions on residential project requirements and goals.",
        features: [
            {
                id: 17,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/progress_tracking.svg",
                feature: "Progress Tracking",
                description: "Monitor the progress of the project in real-time and stay informed about any updates."
            },
            {
                id: 18,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/document_review.svg",
                feature: "Document Review",
                description: "Review and approve documents, blueprints, and other project-related materials."
            },
            {
                id: 19,
                use_case_id: 6,
                icon: "/assets/images/website/use-case-page/feature-icons/design_customization.svg",
                feature: "Design Customization",
                description: "Provide input on design choices to align with personal preferences and lifestyle."
            }
        ],
        status: 1
    },
    {
        id: 8,
        role: "Building Inspector",
        description: "Ensures that construction projects comply with building codes, regulations, and safety standards through inspections and evaluations.",
        features: [
            {
                id: 20,
                use_case_id: 8,
                icon: "/assets/images/website/use-case-page/feature-icons/compliance_check.svg",
                feature: "Compliance Check",
                description: "Ensure that construction projects meet all regulatory and safety standards."
            },
            {
                id: 21,
                use_case_id: 8,
                icon: "/assets/images/website/use-case-page/feature-icons/inspection_report.svg",
                feature: "Inspection Report",
                description: "Create detailed inspection reports highlighting any issues or compliance gaps found."
            }
        ],
        status: 1
    },
    {
        id: 9,
        role: "Engineer",
        description: "Applies scientific principles to the design and construction of buildings, focusing on structural integrity and engineering efficiency.",
        features: [
            {
                id: 22,
                use_case_id: 9,
                icon: "/assets/images/website/use-case-page/feature-icons/design_review.svg",
                feature: "Design Review",
                description: "Review and refine technical designs to ensure they meet engineering standards."
            },
            {
                id: 23,
                use_case_id: 9,
                icon: "/assets/images/website/use-case-page/feature-icons/specification_management.svg",
                feature: "Specification Management",
                description: "Manage and document specifications for materials and construction techniques."
            }
        ],
        status: 0
    },
    {
        id: 10,
        role: "Surveyor",
        description: "Measures and maps out the land or construction site, ensuring that the project is built in the correct location and to scale.",
        features: [
            {
                id: 24,
                use_case_id: 10,
                icon: "/assets/images/website/use-case-page/feature-icons/land_survey.svg",
                feature: "Land Survey",
                description: "Conduct precise land surveys to determine property boundaries and topographical data."
            },
            {
                id: 25,
                use_case_id: 10,
                icon: "/assets/images/website/use-case-page/feature-icons/site_analysis.svg",
                feature: "Site Analysis",
                description: "Analyze the site conditions to assess suitability for construction and identify potential challenges."
            }
        ],
        status: 0
    },
    {
        id: 11,
        role: "Quality Control Specialist",
        description: "Monitors the quality of construction materials and workmanship, ensuring they meet the project's standards and specifications.",
        features: [
            {
                id: 26,
                use_case_id: 11,
                icon: "/assets/images/website/use-case-page/feature-icons/quality_assurance.svg",
                feature: "Quality Assurance",
                description: "Ensure that construction materials and workmanship meet the required standards and specifications."
            },
            {
                id: 27,
                use_case_id: 11,
                icon: "/assets/images/website/use-case-page/feature-icons/testing_inspection.svg",
                feature: "Testing & Inspection",
                description: "Conduct tests and inspections to verify the quality and integrity of materials and structures."
            }
        ],
        status: 0
    },
    {
        id: 12,
        role: "Health and Safety Officer",
        description: "Oversees safety measures on construction sites to prevent accidents and ensure compliance with health and safety regulations.",
        features: [
            {
                id: 28,
                use_case_id: 12,
                icon: "/assets/images/website/use-case-page/feature-icons/safety_protocol.svg",
                feature: "Safety Protocol Management",
                description: "Develop and implement safety protocols to protect workers and ensure a safe construction site."
            },
            {
                id: 29,
                use_case_id: 12,
                icon: "/assets/images/website/use-case-page/feature-icons/hazard_identification.svg",
                feature: "Hazard Identification",
                description: "Identify potential hazards on-site and take measures to mitigate risks before accidents occur."
            }
        ],
        status: 0
    },
    {
        id: 13,
        role: "Real Estate Agent",
        description: "Facilitates the buying, selling, or renting of properties, providing clients with property listings and market insights.",
        features: [
            {
                id: 30,
                use_case_id: 13,
                icon: "/assets/images/website/use-case-page/feature-icons/property_listing.svg",
                feature: "Property Listing",
                description: "List available properties for sale or rent, including detailed descriptions and pricing."
            },
            {
                id: 31,
                use_case_id: 13,
                icon: "/assets/images/website/use-case-page/feature-icons/client_management.svg",
                feature: "Client Management",
                description: "Manage client interactions, inquiries, and follow-ups to facilitate the property buying or renting process."
            }
        ],
        status: 0
    },
    {
        id: 14,
        role: "Legal Advisor",
        description: "Provides legal advice and support on construction contracts, compliance issues, and other legal aspects of real estate projects.",
        features: [
            {
                id: 32,
                use_case_id: 14,
                icon: "/assets/images/website/use-case-page/feature-icons/compliance_guidance.svg",
                feature: "Compliance Guidance",
                description: "Provide legal guidance to ensure that all construction projects comply with local laws and regulations."
            },
            {
                id: 33,
                use_case_id: 14,
                icon: "/assets/images/website/use-case-page/feature-icons/contract_review.svg",
                feature: "Contract Review",
                description: "Review and draft contracts to protect the interests of stakeholders and ensure fair terms."
            }
        ],
        status: 0
    },
    {
        id: 15,
        role: "Procurement Manager",
        description: "Manages the purchasing of materials and equipment for construction projects, negotiating with suppliers for the best deals.",
        features: [
            {
                id: 34,
                use_case_id: 15,
                icon: "/assets/images/website/use-case-page/feature-icons/vendor_management.svg",
                feature: "Vendor Management",
                description: "Manage relationships with vendors and suppliers to ensure timely delivery of materials and services."
            },
            {
                id: 35,
                use_case_id: 15,
                icon: "/assets/images/website/use-case-page/feature-icons/purchase_order_tracking.svg",
                feature: "Purchase Order Tracking",
                description: "Track the status of purchase orders to ensure materials are delivered on time and within budget."
            }
        ],
        status: 0
    }
];
